import { useEffect, useMemo, useRef, useState } from 'react'
import { isEmpty } from 'lodash'
import ReCAPTCHA from 'react-google-recaptcha'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import routes from '../../routers'
import { removeUserTokenFromLocalStorage, setUserRoleStorage, updateToken } from '../../utils/auth'
import {
  EMAIL_PATTERN,
  REQUIRED_FIELD_MESSAGE,
  ROLES,
  SITE_KEY,
  TEMPORARY_TOKEN,
} from '../../utils/constants'
import { confirmEmail, getUserInfo } from '../../api/auth'
import { getUserGroups } from '../../api/group'
import { setRoleUser, setUser } from '../../redux/actions/user'
import { setGroups } from '../../redux/actions/groups'
import { Lock, Mail } from '../../components/SvgIcon'
import useLogin from '../../utils/hooks/useLogin'
import ResendConfirmEmail from '../../components/ResendConfirmEmail'
import LayoutRegistration from '../../components/LayoutRegistration'
import HookForm from '../../components/HookForm'

const SignIn = () => {
  const loginFormRef = useRef(null)
  const { handleLogin } = useLogin()
  const [query] = useSearchParams()
  const [linkToSignUp, setLinkToSignUp] = useState(routes.signUp)
  const [disableBtn, setDisableBtn] = useState(true)
  const [confirmNote, setConfirmNote] = useState('')
  // An address that still needs confirming: offer to resend the email
  const [resendTo, setResendTo] = useState('')
  const signedInUser = useSelector((state) => state.user?.data)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const code = query.get('confirm')
  const { idGroup, slugGroup } = { idGroup: query.get('idGroup'), slugGroup: query.get('slug') }

  const defaultFields = {
    className: 'mt-8',
    defaultValue: '',
  }

  const fields = [
    {
      name: 'login',
      rules: {
        required: REQUIRED_FIELD_MESSAGE,
        pattern: {
          value: EMAIL_PATTERN,
          message: 'This field must have valid email',
        },
      },
      icon: <Mail />,
      nameLabel: 'Email Address',
      inputType: 'email',
      ...defaultFields,
    },
    {
      name: 'password',
      rules: {
        required: REQUIRED_FIELD_MESSAGE,
        minLength: { value: 6, message: 'Password must be at least 6 characters' },
      },
      icon: <Lock />,
      nameLabel: 'Password',
      inputType: 'password',
      ...defaultFields,
    },
  ]

  const handleSubmit = (data) => {
    setResendTo('')
    handleLogin(data, loginFormRef, idGroup, slugGroup).catch((error) => {
      // 10002: signed up but hasn't confirmed their email yet
      if (error?.response?.data?.code === 10002) setResendTo(data.login)
    })
  }

  const Footer = useMemo(() => {
    return (
      <div>
        <div className="innerRecaptcha">
          <ReCAPTCHA onChange={() => setDisableBtn(false)} sitekey={SITE_KEY} size="normal" />
        </div>
        <button disabled={disableBtn} type="submit" className="buttonWrapper buttonText">
          SIGN IN
        </button>
        <Link to={routes.resetPassword} className="modalForget opacity">
          Forget your password?
        </Link>
      </div>
    )
  }, [disableBtn])

  useEffect(() => {
    removeUserTokenFromLocalStorage(TEMPORARY_TOKEN)
    if (!isEmpty(code)) {
      const decodeCode = window.atob(code) || ''
      const [codeConfirm, login] = decodeCode.split(':')
      // Signed in already (most people): back into the app, where the banner shows
      // if the link didn't work
      const backToApp = () =>
        getUserInfo()
          .then(({ data }) => dispatch(setUser(data)))
          .catch(() => null)
          .finally(() => navigate(routes.userProfile))
      // Confirmed from the email: signed straight in, to the welcome screen. Only if that
      // fails do they need to type their password.
      const signInFromLink = (token) => {
        updateToken(token)
        setUserRoleStorage(ROLES.ROLE_USER)
        dispatch(setRoleUser(ROLES.ROLE_USER))
        return getUserInfo()
          .then(({ data }) => dispatch(setUser(data)))
          .then(() =>
            getUserGroups()
              .then(({ data }) => dispatch(setGroups(data)))
              .catch(() => null)
          )
          .then(() => navigate(routes.userProfile, { state: { welcome: true } }))
          .catch(() => {
            updateToken()
            setUserRoleStorage(ROLES.ROLE_GUEST)
            dispatch(setRoleUser(ROLES.ROLE_GUEST))
            setConfirmNote('Thanks, your email is confirmed. Sign in below.')
          })
      }
      confirmEmail({ code: codeConfirm, login: login })
        .then(({ data } = {}) => {
          if (signedInUser) return backToApp()
          if (data?.token) return signInFromLink(data.token)
          setConfirmNote('Thanks, your email is confirmed. Sign in below.')
        })
        .catch((e) => {
          if (signedInUser) return backToApp()
          // 10005: this email was already confirmed
          if (e?.response?.data?.code === 10005) {
            return setConfirmNote('Your email is already confirmed. Sign in below.')
          }
          setConfirmNote('That confirmation link has expired.')
          setResendTo(login || '')
        })
    }
    if (idGroup && slugGroup) {
      setLinkToSignUp(`${routes.signUp}?idGroup=${idGroup}&slug=${slugGroup}`)
    }
  }, [])

  return (
    <LayoutRegistration routesRedirect={linkToSignUp} title="Sign-In">
      <div className="modalSignUp">
        <p className="modalTitle">Sign in to your account</p>
        <p className="modalSubTitle">
          or sign up for a{' '}
          <Link to={linkToSignUp} className="subTitleSignUp opacity">
            free account
          </Link>
        </p>
        {confirmNote ? (
          <p className="modalSubTitle" role="status">
            {confirmNote}
          </p>
        ) : null}
        <HookForm
          ref={loginFormRef}
          className="formSignUp"
          onSubmit={handleSubmit}
          fields={fields}
          Footer={Footer}
        />
        <ResendConfirmEmail key={resendTo} email={resendTo} />
      </div>
    </LayoutRegistration>
  )
}
export default SignIn
